import {
  Box,
  Tab as ChakraTab,
  Table,
  TableContainer,
  TabPanel,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { styles } from '@ui/components/TanstackTable';
import { actions } from '@ui/features/quote-builder/state/reducer';
import { selectQuoteBuilder } from '@ui/features/quote-builder/state/selectors/quote-builder';
import { selectQuoteLineWithLeadTimeIssues } from '@ui/features/quote-builder/state/selectors/quote-line';
import { QuoteInfoTab } from '@ui/features/quote-builder/types/state/quote-line';
import { useDispatch } from '@ui/hooks/useDispatch';
import { useSelector } from '@ui/hooks/useSelector';

import { useQuoteLineContext } from '../../QuoteLineProvider';
import { TabComponentType } from '../types';
import { generateCostBreaks } from './generateCostBreaks';
import { SequenceRows } from './SequenceRows';

const tab = QuoteInfoTab.CostBreaks;

function Tab() {
  const dispatch = useDispatch();
  const { quoteLineUuid } = useQuoteLineContext();

  function handleTabChange() {
    dispatch(actions.quoteLineInfoTabChanged({ quoteLineUuid, tab }));
  }

  return <ChakraTab onClick={handleTabChange}>Cost Breaks</ChakraTab>;
}

export function CostBreaksPanel() {
  const calculationConfig = useSelector(
    (state) => selectQuoteBuilder(state).calculationConfig,
  );
  const { quoteLineUuid } = useQuoteLineContext();
  const { quotePrices, quotePriceUuids, snapWorkOrder } = useSelector((state) =>
    selectQuoteLineWithLeadTimeIssues(state, quoteLineUuid),
  );

  if (!snapWorkOrder) {
    return null;
  }

  const { sequences, quotePriceCosts } = generateCostBreaks(
    {
      quotePriceUuids,
      quotePrices,
      snapWorkOrder,
    },
    calculationConfig,
  );

  return (
    <Box width="100%" overflow="hidden">
      <TableContainer>
        <Table sx={styles}>
          <Thead>
            <Tr>
              <Th>Sequence</Th>
              <Th>Category</Th>

              {/* Quantities */}
              {Object.values(quotePriceCosts).map(
                ({ quotePriceUuid, quantity }) => (
                  <Th key={quotePriceUuid}>{quantity}</Th>
                ),
              )}
            </Tr>
          </Thead>
          <Tbody>
            <SequenceRows
              sequences={sequences}
              quotePriceCosts={quotePriceCosts}
            />

            <Tr>
              <Td />
              <Td>Unit Cost</Td>
              {Object.values(quotePriceCosts).map(
                ({ quotePriceUuid, unitCost }) => (
                  <Td key={`${quotePriceUuid}-unitCost`}>{unitCost}</Td>
                ),
              )}
            </Tr>
            <Tr>
              <Td />
              <Td>Total Cost</Td>
              {Object.values(quotePriceCosts).map(
                ({ quotePriceUuid, totalCost }) => (
                  <Td key={`${quotePriceUuid}-totalCost`}>{totalCost}</Td>
                ),
              )}
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function Panel() {
  return (
    <TabPanel>
      <CostBreaksPanel />
    </TabPanel>
  );
}

export const CostBreaks = {
  tab,
  Tab,
  Panel,
} satisfies TabComponentType;
